import { PostQuery, PostQueryVariables, RepliesQuery } from '__generated__/graphql/legacy/graphql';
import { getTotalNumberOfReplies } from 'helpers/postHelpers';
import { useWindowSize } from 'hooks/useWindowSize';
import MenuTab from 'koba/components/MobileMenu/components/MenuTab';
import { useTranslation } from 'react-i18next';
import {
  COMMENTS_PER_PAGE,
  NESTED_REPLIES_PER_PAGE,
  NUMBER_OF_REACTIONS,
  SCREEN_LG,
} from 'utils/constants';

import React, { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client';

import { Button, Text } from '@thinkific/chalk';
import { X, Yarn } from '@thinkific/chalk/react-icons';

import CommentList from 'components/CommentList';
import { useCommunityContext } from 'components/CommunityContext';
import PostContainer from 'components/PostContainer/PostContainer';
import { useReplyEditorContext } from 'components/ReplyEditorContext/ReplyEditorContext';
import RichTextEditor from 'components/RichTextEditor/RichTextEditor';

import { POST_QUERY } from 'schema/Post/queries';
import { REPLIES_QUERY } from 'schema/Replies/queries';

import { replyHeaderIconStyle, replyPanelHeaderStyle, replyPanelStyle } from './ReplyPanel.css';
import styles from './ReplyPanel.module.scss';

const ReplyPanel = ({ isOpen, onClose, postId }) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const { community } = useCommunityContext();
  const replyScrollableRef = useRef<HTMLDivElement>(null);
  const {
    control,
    handleSubmit,
    resetForm,
    onSubmit,
    errors,
    setValue,
    editorRef,
    isSubmitting,
    updatePostId,
    replyPlaceholder,
    closeReplyOnComment,
    isEditing,
    cancelEditing,
    mentionsTextAreaRef,
  } = useReplyEditorContext();

  const isMobile = width < SCREEN_LG;

  const { data: postData } = useQuery<PostQuery, PostQueryVariables>(POST_QUERY, {
    variables: {
      communityId: community.id,
      postId,
      numberOfReactions: NUMBER_OF_REACTIONS,
    },
    notifyOnNetworkStatusChange: true,
    skip: !postId,
  });

  const post = postData?.community?.post;

  const repliesCount = getTotalNumberOfReplies(post || undefined);

  const { loading, data, fetchMore } = useQuery<RepliesQuery>(REPLIES_QUERY, {
    variables: {
      communityId: community.id,
      postId,
      numOfReplies: COMMENTS_PER_PAGE,
      numOfNestedReplies: NESTED_REPLIES_PER_PAGE,
      numberOfReactions: NUMBER_OF_REACTIONS,
    },
    skip: !postId,
  });

  useEffect(() => {
    if (postId) {
      updatePostId(postId);
    }
  }, [postId]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    if (isOpen && mentionsTextAreaRef.current) {
      timeoutId = setTimeout(() => {
        if (mentionsTextAreaRef.current) {
          mentionsTextAreaRef.current.focus();
        }
      }, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, mentionsTextAreaRef]);

  useEffect(() => {
    // reset editor content when the reply panel is closed
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  return (
    <MenuTab
      hasHeader={isMobile}
      isOpen={isOpen}
      menuTitle={t('components-replyPanel-threads', 'Threads')}
      size={isMobile ? 'full' : 'x-large'}
      slideTo="left"
      wrapperClassName={replyPanelStyle}
      hasOverlay
      onClose={onClose}
    >
      <aside
        aria-label="Threads"
        className={styles['reply-panel__wrapper']}
        data-qa="reply-panel__wrapper"
        role="dialog"
      >
        {!isMobile && (
          <div className={replyPanelHeaderStyle}>
            <div className={replyHeaderIconStyle}>
              <Yarn />
              <Text variant="body.xl">{t('components-replyPanel-threads', 'Threads')}</Text>
            </div>
            <div>
              <Button emphasis="low" icon={X} size="sm" hiddenLabel onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
        {post && (
          <>
            <div className={styles['reply-panel__content']} ref={replyScrollableRef}>
              <PostContainer
                author={post.user}
                content={post.content || ''}
                key={post.id}
                mentionedUsers={post?.mentionedUsers}
                post={post}
                refetchPostList={fetchMore}
                showActions={false}
                t={t}
                isReplyPanel
              />

              <div className={styles['reply-panel__reply-count']}>
                <Text color="action.neutral.base" variant="body.xs">
                  {repliesCount > 0
                    ? `${repliesCount} ${t('Replies', 'Replies')}`
                    : t('components-replyPanel-no_replies_yet', 'No replies yet')}
                </Text>
                <span className={styles['reply-panel__divider']} />
              </div>

              <CommentList
                containerRef={replyScrollableRef}
                data={data}
                fetchMore={fetchMore}
                loading={loading}
                parentPostId={post.id}
                t={t}
                onPostChange={() => {}}
              />
            </div>
            <RichTextEditor
              cancelEdit={cancelEditing}
              closeReplyOnComment={closeReplyOnComment}
              contentPlaceholder={`${t(
                'components-replyPanel-reply_to',
                'Reply to'
              )} ${post?.user?.fullName}`}
              // @ts-ignore
              control={control}
              errors={errors}
              isEditing={isEditing}
              isSubmitting={isSubmitting}
              mentionsTextAreaRef={mentionsTextAreaRef}
              postType="reply"
              ref={editorRef}
              replyPlaceholder={replyPlaceholder}
              setValue={setValue}
              t={t}
              onSubmit={handleSubmit(onSubmit)}
            />
          </>
        )}
      </aside>
    </MenuTab>
  );
};

export default ReplyPanel;
